import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cart from "../assets/images/support/cart.png";
import credit_card from "../assets/images/support/credit-card.png";
import dish from "../assets/images/support/dish.png";
import help_circle from "../assets/images/support/help-circle.png";
import scooter from "../assets/images/support/scooter.png";
import arrow from "../assets/images/location-modal/arrow-right.png";
import { orderService, supportService } from "../services";
import { OrderI } from "../utils/types";
import { SupportThreadType, getLastMessagePreview } from "../utils/support";
import { useAuth } from "../context/AuthContext";
import socketClient from "../utils/socketClient";
import { SOCKET_EVENTS } from "../utils/socketEvents";

const ORDERS_LIMIT = 5;

const HELP_CATEGORIES = [
  { title: "Payments & Refunds", topic: "Payments & Refunds", icon: help_circle },
  { title: "Delivery & Address Issues", topic: "Delivery & Address Issues", icon: scooter },
  { title: "Restaurant & Food Quality", topic: "Restaurant & Food Quality", icon: dish },
  { title: "Offers & Coupons", topic: "Offers & Coupons", icon: credit_card },
];

const formatOrderDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const day = d.getDate().toString().padStart(2, "0");
  const month = months[d.getMonth()];
  const hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  return `${day} ${month}, ${h}:${minutes} ${ampm}`;
};

const formatStatus = (status: string): string =>
  status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const Help_Support = () => {
  const navigate = useNavigate();
  const { checkAuth, openSigninModal } = useAuth();
  const [ordersExpanded, setOrdersExpanded] = useState(true);
  const [orders, setOrders] = useState<OrderI[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [thread, setThread] = useState<SupportThreadType | null>(null);

  const openSupportChat = (params?: {
    topic?: string;
    orderId?: string;
    initialMessage?: string;
  }) => {
    if (!checkAuth()) {
      openSigninModal();
      return;
    }

    const search = new URLSearchParams();
    if (params?.topic) search.set("topic", params.topic);
    if (params?.orderId) search.set("orderId", params.orderId);
    if (params?.initialMessage) search.set("initialMessage", params.initialMessage);
    const qs = search.toString();
    navigate(qs ? `/support/chat?${qs}` : "/support/chat");
  };

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, threadsRes] = await Promise.all([
        orderService.getOrders({ page: 1, limit: ORDERS_LIMIT }) as Promise<any>,
        supportService.getThreads(1, 1) as Promise<any>,
      ]);

      if (ordersRes?.status && Array.isArray(ordersRes.data)) {
        setOrders(ordersRes.data);
      } else if (Array.isArray(ordersRes?.data)) {
        setOrders(ordersRes.data);
      }

      if (threadsRes?.status && Array.isArray(threadsRes.data)) {
        setThread(threadsRes.data[0] ?? null);
      }
    } catch (error) {
      console.error("Failed to load help and support data:", error);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!checkAuth()) {
      setOrdersLoading(false);
      return;
    }
    setOrdersLoading(true);
    fetchData();
    socketClient.connect();
  }, [checkAuth, fetchData]);

  useEffect(() => {
    if (!checkAuth()) return;

    const refreshThread = () => {
      fetchData();
    };

    socketClient.on(SOCKET_EVENTS.SUPPORT_MESSAGE, refreshThread);
    socketClient.on(SOCKET_EVENTS.SUPPORT_READ, refreshThread);
    socketClient.on(SOCKET_EVENTS.SUPPORT_ROOM_READY, refreshThread);

    return () => {
      socketClient.off(SOCKET_EVENTS.SUPPORT_MESSAGE, refreshThread);
      socketClient.off(SOCKET_EVENTS.SUPPORT_READ, refreshThread);
      socketClient.off(SOCKET_EVENTS.SUPPORT_ROOM_READY, refreshThread);
    };
  }, [checkAuth, fetchData]);

  const unreadCount = thread?.unreadCount || 0;

  const renderOrderCard = (order: OrderI) => {
    const firstItem = order.items?.[0];
    const itemLabel = firstItem
      ? `${firstItem.quantity} x ${firstItem.dishName}`
      : `${order.totalItems || 0} item(s)`;
    const restaurantName = order.restaurant?.businessName ?? "Restaurant";
    const city = order.restaurant?.location?.city;
    const restaurantLabel = city ? `${restaurantName}, ${city}` : restaurantName;

    return (
      <div
        key={order._id}
        className="border dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800"
      >
        <div className="flex justify-between items-start gap-3 border-b border-dashed dark:border-gray-700 pb-2">
          <p className="flex-1 text-[#050505] dark:text-white font-urbanist text-[12px] font-medium leading-[20px]">
            {itemLabel}
          </p>
          <p className="flex-1 text-right text-[#050505] dark:text-white font-urbanist text-[12px] font-medium leading-[20px]">
            {restaurantLabel}
          </p>
        </div>
        <div className="flex justify-between items-end border-b dark:border-gray-700 py-2">
          <div className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[10px] font-medium leading-[17px]">
            <p>Order Placed on {formatOrderDate(order.createdAt)}</p>
            <p>{formatStatus(order.orderStatus || "")}</p>
          </div>
          <p className="text-[#050505] dark:text-white font-urbanist text-[12px] font-medium leading-[20px]">
            ₹{Number(order.totalAmount || 0).toFixed(2)}
          </p>
        </div>
        <div className="flex justify-between items-center pt-2">
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="flex items-center gap-2"
          >
            <span className="text-[#050505] dark:text-white font-urbanist text-[12px] font-medium leading-[20px]">
              All Details
            </span>
            <img src={arrow} alt="" className="h-[18px] w-[18px] dark:invert" />
          </button>
          <button
            type="button"
            onClick={() =>
              openSupportChat({
                orderId: order._id,
                topic: "Order help",
                initialMessage: `Hi, I need help with my order ${order._id}.`,
              })
            }
            className="text-[#7402A7] dark:text-purple-400 font-urbanist text-[12px] font-semibold tracking-wide"
          >
            HELP
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 min-h-[70vh] transition-colors duration-300">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-medium leading-[20px]">
          Profile / Help and Support
        </p>
        <h1 className="text-[#050505] dark:text-white font-urbanist text-[24px] font-semibold leading-[140%] mb-5">
          Help and Support
        </h1>

        <button
          type="button"
          onClick={() => openSupportChat()}
          className="w-full flex items-center gap-3 rounded-[16px] border dark:border-gray-700 bg-white dark:bg-gray-800 p-4 mb-4 text-left"
        >
          <div className="h-11 w-11 rounded-xl bg-[#F3E8FF] dark:bg-purple-950 flex items-center justify-center text-[#7402A7] dark:text-purple-300 text-lg">
            💬
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#050505] dark:text-white font-urbanist text-[16px] font-semibold">
              Chat with Support
            </p>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[12px] line-clamp-2">
              {getLastMessagePreview(thread)}
            </p>
          </div>
          {unreadCount > 0 && (
            <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#7402A7] text-white font-urbanist text-[11px] font-semibold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <img src={arrow} alt="" className="h-[18px] w-[18px] dark:invert" />
        </button>

        <div className="rounded-[16px] border dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <button
            type="button"
            onClick={() => setOrdersExpanded((open) => !open)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-3">
              <img src={cart} alt="" className="h-5 w-5 dark:invert" />
              <span className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium">
                Orders
              </span>
            </div>
            <img
              src={arrow}
              alt=""
              className={`h-[18px] w-[18px] dark:invert transition-transform ${
                ordersExpanded ? "rotate-90" : ""
              }`}
            />
          </button>

          {ordersExpanded && (
            <div className="px-4 pb-4 space-y-3">
              {ordersLoading ? (
                <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm">
                  Loading orders...
                </p>
              ) : orders.length === 0 ? (
                <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm">
                  No recent orders found.
                </p>
              ) : (
                orders.map(renderOrderCard)
              )}
            </div>
          )}

          {HELP_CATEGORIES.map((category) => (
            <button
              key={category.topic}
              type="button"
              onClick={() =>
                openSupportChat({
                  topic: category.topic,
                  initialMessage: `Hi, I need help with ${category.topic}.`,
                })
              }
              className="w-full flex items-center justify-between py-5 px-4 border-t dark:border-gray-700 text-left"
            >
              <div className="flex items-center gap-3">
                <img src={category.icon} alt="" className="h-5 w-5 dark:invert" />
                <span className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium">
                  {category.title}
                </span>
              </div>
              <img src={arrow} alt="" className="h-[18px] w-[18px] dark:invert" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Help_Support;
